// src/widgets/RecentUsersWidgets.jsx
//
// Dua widget dalam satu file (tenant-scoped):
//
//   <LastCreatedUsersWidget filter={{ limit: 10 }} />
//   — User terbaru dibuat
//
//   <LastExpiredUsersWidget filter={{ limit: 10 }} />
//   — User dengan status expired terbaru

import { Link } from 'react-router-dom';
import { UserPlus, UserX } from 'lucide-react';
import { Badge } from '../components/UI';
import Empty from './Empty';
import useWidgetData from '../hooks/useWidgetData';

function fmtDT(v) {
  if (!v) return '—';
  return new Date(v).toLocaleString('id-ID', { hour12: false, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const thStyle = {
  padding: '4px 8px', textAlign: 'left',
  color: 'var(--text-muted)', fontWeight: 700,
  textTransform: 'uppercase', fontSize: 10, whiteSpace: 'nowrap',
};
const tdStyle = { padding: '5px 8px', whiteSpace: 'nowrap' };

function UsersTable({ users, dateField, dateLabel }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr>
            {['Username', 'Tenant', 'Status', dateLabel].map(h => <th key={h} style={thStyle}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id} style={{ borderTop: '1px solid var(--border)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', color: 'var(--text-primary)' }}>{u.username}</td>
              <td style={{ ...tdStyle, color: 'var(--text-muted)' }}>{u.tenant_name || `Tenant #${u.tenant_id ?? '—'}`}</td>
              <td style={tdStyle}><Badge status={u.status} /></td>
              <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{fmtDT(u[dateField])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WidgetShell({ icon: Icon, iconColor, title, viewAllTo, loading, isEmpty, emptyMessage, children }) {
  return (
    <div style={{
      padding: 16, height: '100%', boxSizing: 'border-box',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', overflow: 'auto',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon size={13} color={iconColor} />
          {title}
        </div>
        {viewAllTo && (
          <Link to={viewAllTo} style={{ fontSize: 11, color: 'var(--accent-blue)' }}>
            View all →
          </Link>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {[1, 2, 3].map(i => <div key={i} className="skeleton" style={{ height: 28 }} />)}
        </div>
      ) : isEmpty ? (
        <Empty message={emptyMessage} />
      ) : children}
    </div>
  );
}

// ── LastCreatedUsersWidget ───────────────────────────────────────────────────
export function LastCreatedUsersWidget({ filter = { limit: 10 } }) {
  const { data, loading } = useWidgetData('last_created_users', filter);
  const users = data?.users || data?.items || data?._array || (Array.isArray(data) ? data : []);

  return (
    <WidgetShell icon={UserPlus} iconColor="var(--accent-green)" title="User Terbaru Dibuat"
      viewAllTo="/users" loading={loading} isEmpty={users.length === 0} emptyMessage="Belum ada user baru">
      <UsersTable users={users} dateField="created_at" dateLabel="Dibuat" />
    </WidgetShell>
  );
}

// ── LastExpiredUsersWidget ───────────────────────────────────────────────────
export function LastExpiredUsersWidget({ filter = { limit: 10 } }) {
  const { data, loading } = useWidgetData('last_expired_users', filter);
  const users = data?.users || data?.items || data?._array || (Array.isArray(data) ? data : []);

  return (
    <WidgetShell icon={UserX} iconColor="var(--accent-red)" title="User Expired Terbaru"
      viewAllTo="/users" loading={loading} isEmpty={users.length === 0} emptyMessage="Tidak ada user expired">
      <UsersTable users={users} dateField="expired_at" dateLabel="Expired" />
    </WidgetShell>
  );
}